import {
  HashRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
  useNavigate,
} from 'react-router-dom';
import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import { I18nProvider } from './context/I18nContext';
import { LoginProvider, useLogin } from './context/LoginContext';
import { MatrixClientProvider } from './context/MatrixClientProvider';
import { useMatrixClient } from './context/MatrixContext';
import { LayoutProvider, useLayout } from './context/LayoutContext';
import { NavigationProvider, useNavigation } from './context/NavigationContext';
import { ModalProvider, useModal } from './context/ModalContext';
import { RecoveryProvider } from './context/RecoveryContext/index';
import { SettingsProvider } from './context/SettingsContext';
import { DownloadProvider } from './context/DownloadContext';
import { ToastProvider } from './context/ToastContext';
import { AvatarProvider } from './context/AvatarContext';
import IndexedDBGuard from './components/IndexedDBGuard';
import ToastContainer from './components/Common/ToastContainer';
import VerificationCoordinator from './components/verification/VerificationCoordinator';
import AvatarSync from './components/AvatarSync';
import useDownloadNavigationGuard from './hooks/useDownloadNavigationGuard';
import useAutoLogout from './hooks/useAutoLogout';
import { useWindowWidth } from './hooks/useWindowWidth';
import Menu from './components/Menu/Menu';
import ConversationsPage from './components/Page/Conversations/ConversationsPage';
import ChannelsPage from './components/Page/ChannelsPage';
import ContactsPage from './components/Page/Contacts/ContactsPage';
import LoginPage from './components/Login/LoginPage';
import Modal from './components/Modal/Modal';
import SettingsModal from './components/SettingsModal';
import { RecoveryFlow } from './components/Recovery';
import { ChunkErrorBoundary } from './components/ChunkErrorBoundary/ChunkErrorBoundary';
import { UpdateNotification } from './components/UpdateNotification/UpdateNotification';
import './App.css';

// Wrapper component that provides client to RecoveryProvider
const RecoveryProviderWithClient = ({ children }) => {
  const client = useMatrixClient();
  return (
    <RecoveryProvider client={client}>
      {children}
    </RecoveryProvider>
  );
};

RecoveryProviderWithClient.propTypes = {
  children: PropTypes.node.isRequired,
};

// Protected route component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useLogin();

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '1.2rem',
        color: '#666',
      }}>
        Loading...
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

ProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
};

// Main application layout with outlet for nested routes
const AppLayout = () => {
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const { showInfo } = useModal();
  const navigate = useNavigate();
  const client = useMatrixClient();

  // Track window width to auto-hide menu and sidebars below 700px
  const isWideEnoughForSidebars = useWindowWidth(700);

  // Initialize the auto-logout timer
  useAutoLogout();

  // Activate download navigation guard to prevent accidental cancellation
  useDownloadNavigationGuard();

  // Add avatar cache debug helper in development mode
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      const indexedDBService = require('./services/indexedDB').default;
      const { avatarManager } = require('./utils/avatarManager');

      window.avatarCacheDebug = {
        stats: () => {
          const managerStats = avatarManager.getStats();
          console.log('Avatar Manager Stats:', managerStats);
          return managerStats;
        },
        indexedDbStats: async () => {
          const dbStats = await indexedDBService.getAvatarCacheStats();
          console.log('IndexedDB Cache Stats:', dbStats);
          return dbStats;
        },
        clear: () => {
          avatarManager.clearAll();
          console.log('All avatars cleared (memory + IndexedDB)');
        },
        invalidate: async (userId) => {
          await avatarManager.invalidate(userId);
          console.log(`Avatar invalidated for ${userId}`);
        },
        cache: () => {
          return Array.from(avatarManager.cache.entries()).map(([userId, data]) => ({
            userId,
            refCount: data.refCount,
            age: Date.now() - data.timestamp,
            url: data.blobUrl,
          }));
        },
      };

      // console.log('🔍 Avatar Manager debug helper available via window.avatarCacheDebug');
      // console.log('  - window.avatarCacheDebug.stats()              // Get memory cache statistics');
      // console.log('  - await window.avatarCacheDebug.indexedDbStats() // Get IndexedDB statistics');
      // console.log('  - window.avatarCacheDebug.cache()              // View all cached avatars');
      // console.log('  - window.avatarCacheDebug.clear()              // Clear all avatars');
      // console.log('  - await window.avatarCacheDebug.invalidate(userId) // Invalidate specific avatar');
    }

    return () => {
      if (process.env.NODE_ENV === 'development') {
        delete window.avatarCacheDebug;
      }
    };
  }, [client]);

  // Listen for session invalidation events (logged out from another device)
  useEffect(() => {
    const handleSessionInvalidated = () => {
      // Show user-friendly modal message with callback to navigate to login
      // Note: Backend logout already completed by matrixService.handleSessionInvalidated()
      showInfo(
        'Session Expired',
        'Your session was logged out from another device. Please log in again.',
        () => {
          // Backend logout already completed, just navigate to login page
          navigate('/login', { replace: true });
        },
      );
    };

    window.addEventListener('matrix:session-invalidated', handleSessionInvalidated);

    return () => {
      window.removeEventListener('matrix:session-invalidated', handleSessionInvalidated);
    };
  }, [showInfo, navigate]);

  const {
    navigateToConversation,
    navigateToContact,
    navigateToChannel,
    currentPage,
  } = useNavigation();

  const {
    isMenuExpanded,
    maxMenuWidth,
    minMenuWidth,
  } = useLayout();

  const renderMenu = () => {
    // Hide menu when screen width < 700px
    if (!isWideEnoughForSidebars) return null;

    const style = {
      display: 'flex',
      position: 'absolute',
      top: 0,
      left: 0,
      height: '100%',
      width: maxMenuWidth,
    };

    return (
      <div style={style}>
        <Menu
          currentPage={currentPage}
          onSettingsClick={() => setIsSettingsOpen(true)}
          setToConversations={() => navigateToConversation()}
          setToContacts={() => navigateToContact()}
          setToChannels={() => navigateToChannel()}
        />
      </div>
    );
  };

  const pageStyle = {
    display: 'flex',
    position: 'absolute',
    top: 0,
    right: 0,
    height: '100%',
    // Full width when menu is hidden by responsive constraint
    width: isWideEnoughForSidebars
      ? `calc(100% - ${(isMenuExpanded) ? maxMenuWidth : minMenuWidth}px)`
      : '100%',
  };

  return (
    <div className="app-container">
      {renderMenu()}
      <div className="app-page" style={pageStyle}>
        <Outlet />
      </div>
      <SettingsModal
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
      />
    </div>
  );
};

const AppRouter = () => {
  return (
    <Router>
      <NavigationProvider>
        <Routes>
          <Route path="/" element={<Navigate to="/conversations" replace />} />
          <Route path="/login" element={<LoginPage />} />
          <Route
            element={
              <ProtectedRoute>
                <MatrixClientProvider>
                  <AvatarProvider>
                    <AvatarSync />
                    <DownloadProvider>
                      <RecoveryProviderWithClient>
                        <AppLayout />
                        <RecoveryFlow />
                        <VerificationCoordinator />
                      </RecoveryProviderWithClient>
                    </DownloadProvider>
                  </AvatarProvider>
                </MatrixClientProvider>
              </ProtectedRoute>
            }
          >
            <Route path="/conversations" element={<ConversationsPage />} />
            <Route path="/conversations/new" element={<ConversationsPage />} />
            <Route path="/conversations/:id" element={<ConversationsPage />} />
            <Route path="/contacts" element={<ContactsPage />} />
            <Route path="/contacts/:id" element={<ContactsPage />} />
            <Route path="/channels" element={<ChannelsPage />} />
            <Route path="/channels/new" element={<ChannelsPage />} />
            <Route path="/channels/:id" element={<ChannelsPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/conversations" replace />} />
        </Routes>
      </NavigationProvider>
    </Router>
  );
};

// Wrapper to provide contexts
const AppWithRecovery = () => {
  // RecoveryProvider needs to be inside a component that can access the Matrix client
  // So we'll restructure to have RecoveryProvider inside the authenticated area
  return (
    <LayoutProvider>
      <ModalProvider>
        <ToastProvider>
          <UpdateNotification />
          <AppRouter />
          <Modal />
          <ToastContainer />
        </ToastProvider>
      </ModalProvider>
    </LayoutProvider>
  );
};

const App = () => (
  <ChunkErrorBoundary>
    <IndexedDBGuard>
      <I18nProvider defaultLang="en">
        <SettingsProvider>
          <LoginProvider>
            <AppWithRecovery />
          </LoginProvider>
        </SettingsProvider>
      </I18nProvider>
    </IndexedDBGuard>
  </ChunkErrorBoundary>
);

export default App;
